import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';

import type { UseOnboardingAnalyticsReturn } from '@/models/analytics/features/useOnboardingAnalytics';
import type { PersonalizationFormData } from '@heidi/growth';
import type { PersonalizationStep } from '@heidi/growth/contracts';
import { useMutationPublicTemplateEdit } from './useMutationPublicTemplateEdit';
import { useMutationPublicTemplateGeneration } from './useMutationPublicTemplateGeneration';
import { onboardingFilesAtom } from './useOnboardingFiles';

interface UseTemplateGenerationProps {
  formData: PersonalizationFormData;
  updateFormData: <K extends keyof PersonalizationFormData>(
    key: K,
    value: PersonalizationFormData[K]
  ) => void;
  goToStep: (step: PersonalizationStep) => void;
  steps: PersonalizationStep[];
  analytics: UseOnboardingAnalyticsReturn;
}

const MINIMUM_LOADING_MS = 5000;

function getStepAfterTemplateFlow(steps: PersonalizationStep[]): PersonalizationStep {
  const reviewIndex = steps.indexOf('template-review');
  return (reviewIndex >= 0 ? steps[reviewIndex + 1] : undefined) ?? 'completion';
}

export function useTemplateGeneration({
  formData,
  updateFormData,
  goToStep,
  steps,
  analytics,
}: UseTemplateGenerationProps) {
  const { t } = useTranslations();
  const onboardingFiles = useAtomValue(onboardingFilesAtom);

  const { mutate: generateTemplate, isPending: isGenerating } =
    useMutationPublicTemplateGeneration();

  const { mutate: editTemplate, isPending: isEditing } = useMutationPublicTemplateEdit();

  const generatedTemplateName = formData.generatedTemplateName ?? '';
  const generatedTemplateContent = formData.generatedTemplateStructure ?? '';

  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [madeTemplateChanges, setMadeTemplateChanges] = useState(false);

  /** Snapshot of template content before the most recent successful AI refine (single-step undo). */
  const [undoTemplateContent, setUndoTemplateContent] = useState<string | null>(null);

  const handleCreateTemplate = useCallback(() => {
    analytics.trackTemplateStarted({
      usedSuggestion: false,
      specialty: formData.specialty?.name,
    });

    goToStep('template-loading');

    const loadingStartTime = Date.now();

    const waitAndExecute = (callback: () => void) => {
      const elapsed = Date.now() - loadingStartTime;
      const remaining = Math.max(0, MINIMUM_LOADING_MS - elapsed);
      setTimeout(callback, remaining);
    };

    const fileContents = onboardingFiles
      .filter((f) => f.status === 'done' && f.processedContent)
      .map((f) => f.processedContent as string);

    generateTemplate(
      {
        description: formData.templateDescription,
        specialtyName: formData.specialty?.name,
        country: formData.country?.countryCode,
        fileContents: fileContents.length > 0 ? fileContents : undefined,
      },
      {
        onSuccess: (response) => {
          waitAndExecute(() => {
            analytics.trackTemplateCompleted();
            setUndoTemplateContent(null);
            updateFormData('generatedTemplateName', response.template_name);
            updateFormData('generatedTemplateStructure', response.template_structure);
            goToStep('template-review');
          });
        },
        onError: () => {
          waitAndExecute(() => {
            toast({
              title: t('common.error'),
              description: t('signup.templateGenerationFailed'),
              tone: 'destructive',
            });
            goToStep('template-creation');
          });
        },
      }
    );
  }, [
    formData,
    generateTemplate,
    goToStep,
    t,
    updateFormData,
    analytics,
    onboardingFiles,
  ]);

  const handleSkipTemplate = useCallback(() => {
    analytics.trackStepSkipped('template-creation');
    goToStep(getStepAfterTemplateFlow(steps));
  }, [goToStep, analytics, steps]);

  const handleSaveAndContinue = useCallback(() => {
    analytics.trackTemplateReviewed(madeTemplateChanges);
    goToStep(getStepAfterTemplateFlow(steps));
  }, [goToStep, analytics, madeTemplateChanges, steps]);

  const handleApplyChanges = useCallback(
    (changeRequest: string) => {
      if (!generatedTemplateContent) return;

      const snapshotBeforeEdit = generatedTemplateContent;

      editTemplate(
        {
          existingTemplate: generatedTemplateContent,
          editRequest: changeRequest,
          specialtyName: formData.specialty?.name,
          country: formData.country?.countryCode,
        },
        {
          onSuccess: (response) => {
            setMadeTemplateChanges(true);
            setUndoTemplateContent(snapshotBeforeEdit);
            updateFormData('generatedTemplateStructure', response.template_structure);
          },
          onError: () => {
            toast({
              title: t('common.error'),
              description: t('signup.templateEditFailed'),
              tone: 'destructive',
            });
          },
        }
      );
    },
    [generatedTemplateContent, formData, editTemplate, updateFormData, t]
  );

  const handleUndoLastTemplateEdit = useCallback(() => {
    if (undoTemplateContent === null) return;
    updateFormData('generatedTemplateStructure', undoTemplateContent);
    setUndoTemplateContent(null);
  }, [undoTemplateContent, updateFormData]);

  const handleTemplateNameChange = useCallback(
    (name: string) => {
      updateFormData('generatedTemplateName', name);
    },
    [updateFormData]
  );

  const handleTemplateContentChange = useCallback(
    (content: string) => {
      setMadeTemplateChanges(true);
      updateFormData('generatedTemplateStructure', content);
      setUndoTemplateContent(null);
    },
    [updateFormData]
  );

  return {
    generatedTemplateName,
    generatedTemplateContent,
    isEditingTemplate,
    isGenerating,
    isEditing,
    handleCreateTemplate,
    handleSkipTemplate,
    handleSaveAndContinue,
    handleApplyChanges,
    handleUndoLastTemplateEdit,
    canUndoLastTemplateEdit: undoTemplateContent !== null,
    handleTemplateNameChange,
    handleTemplateContentChange,
    setIsEditingTemplate,
  };
}

export type UseTemplateGenerationReturn = ReturnType<typeof useTemplateGeneration>;
